import { Dices } from 'lucide-react';
import { fmt } from '../measure/numFormat';

// Monte-Carlo histogram. Renders the empirical distribution produced
// by the store's calculate() in MC mode (m0.mc.histogram). Adds:
//   - mean (μ) marker — solid white-ish vertical line
//   - worst-case markers — amber dashed lines at WC.min / WC.max so
//     the user sees where the conservative bounds sit relative to
//     the sampled distribution.
//
// The mc data shape comes from runMonteCarlo (utils/monteCarlo.js):
//   {
//     mean, stddev, sampleCount,
//     histogram: { counts: number[], edges: number[], bins: number },
//     percentiles: { p1, p5, p50, p95, p99 },
//     …
//   }
// `min`/`max` for the chart range come from the store's m0.

export default function Histogram({ mc, m0, wc }) {
  if (!mc || !mc.histogram || !mc.histogram.counts?.length) return null;

  const w = 360, h = 160, padX = 14, padY = 14;
  const counts = mc.histogram.counts;
  const bins = counts.length;
  const maxCount = Math.max(...counts);
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;

  // Axis range must include the worst-case bounds, otherwise the WC
  // markers fall outside the chart and the user only sees one (or
  // neither) of the dashed amber lines. A 4 % side-padding keeps the
  // markers from sitting directly on the chart edge.
  const axisMin = wc ? Math.min(m0.min, wc.min) : m0.min;
  const axisMax = wc ? Math.max(m0.max, wc.max) : m0.max;
  const axisPad = (axisMax - axisMin) * 0.04;
  const axisLo  = axisMin - axisPad;
  const axisHi  = axisMax + axisPad;
  const axisRange = axisHi - axisLo || 1;
  const xFor = (v) => padX + ((v - axisLo) / axisRange) * innerW;

  // Bars stay within the MC distribution's [m0.min, m0.max] segment,
  // mapped through the extended axis so they keep their data position
  // even when the chart was widened to fit the WC bounds.
  const histX0 = xFor(m0.min);
  const histX1 = xFor(m0.max);
  const barW = (histX1 - histX0) / bins;

  return (
    <div
      data-snippet="histogramm"
      className="bg-surface border border-line"
      style={{ borderRadius: 'var(--radius-md)', padding: '14px 14px 10px' }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Dices size={14} className="text-brand" aria-hidden="true" />
          <div className="text-ink-2" style={{ fontSize: 12, fontWeight: 600 }}>
            Monte-Carlo-Verteilung
          </div>
        </div>
        <div className="num text-ink-3" style={{ fontSize: 11 }}>
          {mc.sampleCount.toLocaleString('de-DE')} Samples
        </div>
      </div>

      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
        <defs>
          <linearGradient id="histGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--color-brand)" stopOpacity="0.85" />
            <stop offset="100%" stopColor="var(--color-brand)" stopOpacity="0.25" />
          </linearGradient>
        </defs>
        {/* Baseline */}
        <line x1={padX} x2={w - padX} y1={h - padY} y2={h - padY}
              stroke="var(--color-line)" strokeWidth="1" />
        {/* Bars */}
        {counts.map((c, i) => {
          const bh = (c / maxCount) * innerH;
          return (
            <rect
              key={i}
              x={histX0 + i * barW + 0.5}
              y={h - padY - bh}
              width={Math.max(1, barW - 1)}
              height={bh}
              fill="url(#histGrad)"
              rx="1"
            />
          );
        })}
        {/* Mean line */}
        <line
          x1={xFor(mc.mean)} x2={xFor(mc.mean)} y1={padY} y2={h - padY}
          stroke="var(--color-ink)" strokeWidth="1.2" strokeDasharray="3 3"
        />
        {/* Worst-case bounds */}
        {wc && (
          <>
            <line x1={xFor(wc.min)} x2={xFor(wc.min)} y1={padY} y2={h - padY}
                  stroke="var(--color-warn)" strokeWidth="1" strokeDasharray="2 3" opacity="0.7" />
            <line x1={xFor(wc.max)} x2={xFor(wc.max)} y1={padY} y2={h - padY}
                  stroke="var(--color-warn)" strokeWidth="1" strokeDasharray="2 3" opacity="0.7" />
          </>
        )}
      </svg>

      <div
        className="num text-ink-3"
        style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, padding: '0 4px', marginTop: 2 }}
      >
        <span>{fmt(axisMin, 3)}</span>
        <span className="text-ink-2" style={{ fontWeight: 600 }}>μ = {fmt(mc.mean, 3)}</span>
        <span>{fmt(axisMax, 3)}</span>
      </div>
      <div className="text-ink-3" style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span className="bg-ink" style={{ width: 10, height: 2, display: 'inline-block' }} /> μ
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span className="bg-warn" style={{ width: 10, height: 2, display: 'inline-block', opacity: 0.7 }} /> Worst case
        </span>
        <span className="num" style={{ marginLeft: 'auto' }}>σ = {fmt(mc.stddev, 4)}</span>
      </div>
    </div>
  );
}
